import { useEffect, useState } from "react";

function isUsableWindow(candidate) {
  try {
    if (!candidate) return false;
    if (typeof candidate !== "object") return false;
    // In Firefox, accessing properties on a "dead object" throws.
    // The try/catch ensures we don't propagate NS_ERROR_UNEXPECTED.
    // Checking a primitive property like "closed" is cheap.
    if (typeof candidate.closed !== "boolean") {
      // Some Window objects may not expose "closed"; still test basic props.
      void candidate.innerWidth;
      void candidate.innerHeight;
    }
    return true;
  } catch (_) {
    return false;
  }
}

function getSafeWindow(providedWindow) {
  const globalWindow = typeof window !== "undefined" ? window : undefined;
  const selected = providedWindow ?? globalWindow;
  return isUsableWindow(selected) ? selected : undefined;
}

function getWindowDimensions(providedWindow) {
  const w = getSafeWindow(providedWindow);
  if (!w) {
    return { width: undefined, height: undefined };
  }
  try {
    return { width: w.innerWidth, height: w.innerHeight };
  } catch (_) {
    return { width: undefined, height: undefined };
  }
}

export function useWindowDimensions(providedWindow) {
  const [windowDimensions, setWindowDimensions] = useState(
    getWindowDimensions(providedWindow),
  );

  useEffect(() => {
    const w = getSafeWindow(providedWindow);
    if (!w) return;

    function handleResize() {
      setWindowDimensions(getWindowDimensions(providedWindow));
    }

    try {
      w.addEventListener("resize", handleResize);
    } catch (_) {
      // Ignore if adding the listener fails due to a dead window
    }
    return () => {
      try {
        w.removeEventListener("resize", handleResize);
      } catch (_) {
        // Ignore if removal fails due to a dead window
      }
    };
  }, [providedWindow]);

  return windowDimensions;
}
